import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Radio,
  X,
  Send,
  Heart,
  Loader2,
  MessagesSquare,
  Sparkles,
} from "lucide-react";
import { selectAppUser } from "../../redux/appAuthSlice";
import {
  fetchChats,
  sendChat,
  sendLike,
  clearChats,
  clearChatError,
  selectBetChats,
  selectBetChatLoading,
  selectBetChatSending,
  selectBetChatError,
  selectBetChatLiveDrafts,
  joinBetChatRoom,
  leaveBetChatRoom,
  emitBetChatDraft,
} from "../../redux/betChatSlice";
import { toIdStr } from "../../utils/houseAccess";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import { formatRelativeTime } from "../../utils/formatRelativeTime";

const QUICK_EMOJIS = ["👍", "🔥", "😂", "🎯", "💪", "⚡"];

const LiveDraftBubble = ({ draft }) => (
  <motion.div
    className="bc-draft"
    initial={{ opacity: 0, y: 10, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 6, scale: 0.98 }}
    transition={{ type: "spring", damping: 24, stiffness: 320 }}
    layout
  >
    <img
      src={generateAvatarUrl(draft.name)}
      alt=""
      className="bc-msg-avatar bc-draft-avatar"
    />
    <div className="bc-draft-bubble">
      <div className="bc-draft-meta">
        <span className="bc-draft-name">{draft.name}</span>
        <span className="bc-draft-badge">
          <span className="bc-draft-pulse" />
          typing live
        </span>
      </div>
      <p className="bc-draft-text">
        {draft.text}
        <span className="bc-draft-caret" aria-hidden />
      </p>
    </div>
  </motion.div>
);

const BetChatModal = ({ open, onClose, house }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectAppUser);
  const chats = useSelector(selectBetChats);
  const loading = useSelector(selectBetChatLoading);
  const sending = useSelector(selectBetChatSending);
  const error = useSelector(selectBetChatError);
  const liveDrafts = useSelector(selectBetChatLiveDrafts);

  const [inputText, setInputText] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const betId = toIdStr(house?._id);
  const betName = house?.name || "Bet house";
  const userId = toIdStr(user?._id);
  const canSend = Boolean(inputText.trim());

  const draftList = useMemo(
    () => Object.values(liveDrafts || {}).filter((d) => d?.text),
    [liveDrafts]
  );

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !user || !betId) return undefined;

    dispatch(fetchChats(betId));
    dispatch(
      joinBetChatRoom({
        betId,
        userId: toIdStr(user._id),
        name: user?.name,
      })
    );

    return () => {
      dispatch(leaveBetChatRoom({ betId }));
    };
  }, [open, user, betId, dispatch]);

  useEffect(() => {
    if (open && user) scrollToBottom();
  }, [open, user, chats.length, draftList.length, scrollToBottom]);

  useEffect(() => {
    if (!open) {
      dispatch(clearChats());
      setInputText("");
    }
  }, [open, dispatch]);

  const publishDraft = useCallback(
    (text) => {
      if (!userId || !betId) return;
      dispatch(
        emitBetChatDraft({
          betId,
          userId,
          name: user?.name,
          text,
        })
      );
    },
    [betId, dispatch, user?.name, userId]
  );

  const handleInputChange = (e) => {
    const next = e.target.value;
    setInputText(next);
    publishDraft(next);
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || !userId || !betId || sending) return;

    publishDraft("");
    try {
      await dispatch(sendChat({ betId, userId, message: text })).unwrap();
      setInputText("");
      scrollToBottom();
    } catch {
      /* error in slice */
    }
  };

  const toggleLike = (messageId) => {
    if (!userId) return;
    dispatch(sendLike({ messageId: toIdStr(messageId), userId }));
  };

  const handleLogin = () => {
    onClose();
    navigate("/app/login");
  };

  const renderMessage = (item) => {
    const authorId = toIdStr(item.userId?._id ?? item.userId);
    const isMine = authorId === userId;
    const authorName = item.userId?.name || "Bettor";
    const liked = (item.likes || []).some(
      (l) => toIdStr(l.userId) === userId
    );

    return (
      <motion.div
        key={item._id}
        className={`bc-msg ${isMine ? "bc-msg-mine" : ""}`}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
      >
        {!isMine && (
          <img
            src={generateAvatarUrl(authorName)}
            alt=""
            className="bc-msg-avatar"
          />
        )}
        <div className={`bc-msg-bubble ${isMine ? "bc-msg-bubble-mine" : ""}`}>
          <p className={`bc-msg-author ${isMine ? "bc-msg-author-mine" : ""}`}>
            {isMine ? "You" : authorName}
          </p>
          <p className="bc-msg-text">{item.message}</p>
          <div className="bc-msg-footer">
            <span className="bc-msg-time">{formatRelativeTime(item.createdAt)}</span>
            {user && (
              <button
                type="button"
                className={`bc-like-btn ${liked ? "bc-like-btn-active" : ""}`}
                onClick={() => !liked && toggleLike(item._id)}
                disabled={liked}
                aria-label={liked ? "Liked" : "Like message"}
              >
                <Heart size={12} fill={liked ? "currentColor" : "none"} />
                <span>{item.likes?.length || 0}</span>
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="bc-root" role="presentation">
          <motion.button
            type="button"
            className="bc-backdrop"
            aria-label="Close chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.section
            role="dialog"
            aria-modal="true"
            aria-labelledby="bc-title"
            className="bc-sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bc-ambient" aria-hidden />
            <div className="bc-handle" aria-hidden />

            <header className="bc-header">
              <div className="bc-header-top">
                <div className="bc-header-brand">
                  <span className="bc-header-icon">
                    <Radio size={15} />
                  </span>
                  <div className="bc-header-text">
                    <div className="bc-eyebrow">
                      <span className="bc-live-dot" />
                      Live floor
                    </div>
                    <h2 id="bc-title" className="bc-title">
                      {betName}
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  className="bc-close"
                  onClick={onClose}
                  aria-label="Close chat"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="bc-subtitle">
                Watch drafts appear as bettors type — then lock them in.
              </p>
            </header>

            {!user ? (
              <div className="bc-gate">
                <div className="bc-gate-icon">
                  <MessagesSquare size={28} />
                </div>
                <p className="bc-gate-title">Sign in to open the floor</p>
                <p className="bc-gate-sub">
                  Join the live house chat after you log in.
                </p>
                <button type="button" className="bc-gate-btn" onClick={handleLogin}>
                  Sign in
                </button>
              </div>
            ) : loading && chats.length === 0 ? (
              <div className="bc-loading">
                <Loader2 size={22} className="animate-spin" />
                <span>Opening the floor…</span>
              </div>
            ) : (
              <div ref={listRef} className="bc-list">
                {chats.length === 0 && draftList.length === 0 ? (
                  <div className="bc-empty">
                    <div className="bc-empty-icon">
                      <Sparkles size={26} />
                    </div>
                    <p className="bc-empty-title">The floor is quiet</p>
                    <p className="bc-empty-sub">
                      Start typing — others will see your words appear live.
                    </p>
                  </div>
                ) : (
                  <>
                    {chats.map(renderMessage)}
                    <AnimatePresence initial={false}>
                      {draftList.map((draft) => (
                        <LiveDraftBubble key={draft.userId} draft={draft} />
                      ))}
                    </AnimatePresence>
                  </>
                )}
              </div>
            )}

            {user && (
              <footer className="bc-footer">
                {draftList.length > 0 && (
                  <div className="bc-live-hint" aria-live="polite">
                    <span className="bc-live-hint-dot" />
                    {draftList.length === 1
                      ? `${draftList[0].name} is writing on the floor`
                      : `${draftList.length} bettors writing live`}
                  </div>
                )}
                <div className="bc-quick-emojis">
                  {QUICK_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="bc-emoji-chip"
                      onClick={() => {
                        setInputText((t) => {
                          const next = t + emoji;
                          publishDraft(next);
                          return next;
                        });
                      }}
                      aria-label={`Add ${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <div className="bc-input-row">
                  <textarea
                    ref={inputRef}
                    className="bc-input"
                    value={inputText}
                    onChange={handleInputChange}
                    placeholder="Type live — others can see it…"
                    rows={1}
                    disabled={sending}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={`bc-send ${canSend ? "bc-send-active" : ""}`}
                    disabled={!canSend || sending}
                    onClick={handleSend}
                    aria-label="Send message"
                  >
                    {sending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
                {error && (
                  <button
                    type="button"
                    className="bc-error"
                    onClick={() => dispatch(clearChatError())}
                  >
                    {error}
                  </button>
                )}
              </footer>
            )}
          </motion.section>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BetChatModal;
